import { ImageIcon, Cross2Icon, UploadIcon } from '@radix-ui/react-icons';
import { useRef, useState, useCallback, KeyboardEvent, DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import cn from '../../../app/utils/cn';
import ProgressBar from '../ProgressBar';

const DEFAULT_ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const DEFAULT_MAX_SIZE = 5 * 1024 * 1024;

interface IImageUploadProps {
  value?: string;
  onFileSelected: (file: File) => void;
  onRemove?: () => void;
  isUploading?: boolean;
  progress?: number;
  error?: string;
  disabled?: boolean;
  acceptedTypes?: string[];
  maxSizeBytes?: number;
  className?: string;
  alt?: string;
}

export default function ImageUpload({
  value,
  onFileSelected,
  onRemove,
  isUploading = false,
  progress = 0,
  error,
  disabled = false,
  acceptedTypes = DEFAULT_ACCEPTED_TYPES,
  maxSizeBytes = DEFAULT_MAX_SIZE,
  className,
  alt,
}: IImageUploadProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragRejected, setDragRejected] = useState(false);
  const [internalError, setInternalError] = useState<string | null>(null);

  const validateFile = useCallback(
    (file: File): string | null => {
      if (!acceptedTypes.includes(file.type)) {
        return t('catalog.imageUpload.errors.invalidType');
      }
      if (file.size > maxSizeBytes) {
        return t('catalog.imageUpload.errors.tooLarge', {
          size: Math.round(maxSizeBytes / 1024 / 1024),
        });
      }
      return null;
    },
    [acceptedTypes, maxSizeBytes, t],
  );

  const handleFileChange = useCallback(
    (file: File) => {
      const validationError = validateFile(file);
      if (validationError) {
        setInternalError(validationError);
        return;
      }
      setInternalError(null);
      onFileSelected(file);
    },
    [validateFile, onFileSelected],
  );

  const handleDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled) return;
      setIsDragOver(true);
      const item = e.dataTransfer.items[0];
      if (item && !acceptedTypes.includes(item.type)) {
        setDragRejected(true);
      } else {
        setDragRejected(false);
      }
    },
    [disabled, acceptedTypes],
  );

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
    setDragRejected(false);
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      setDragRejected(false);
      if (disabled) return;
      const file = e.dataTransfer.files[0];
      if (!file) return;
      handleFileChange(file);
    },
    [disabled, handleFileChange],
  );

  const handleClick = useCallback(() => {
    if (disabled) return;
    inputRef.current?.click();
  }, [disabled]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    },
    [handleClick],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      handleFileChange(file);
      e.target.value = '';
    },
    [handleFileChange],
  );

  const displayError = error ?? internalError;
  const hasImage = !!value;

  return (
    <div className={cn('w-full', className)}>
      <div
        role={!isUploading && !disabled ? 'button' : undefined}
        tabIndex={!isUploading && !disabled ? 0 : undefined}
        aria-disabled={disabled}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={cn(
          'relative flex min-h-32 w-full flex-col items-center justify-center overflow-hidden rounded-lg border-2 border-dashed transition-colors duration-200',
          'border-border-input bg-surface-input',
          !isUploading && !disabled && 'cursor-pointer',
          isDragOver &&
            !dragRejected &&
            'border-green-500 bg-green-50 dark:bg-green-900/20',
          dragRejected && 'border-red-500 bg-red-50 dark:bg-red-900/20',
          !!displayError && 'border-red-500',
          disabled && 'cursor-not-allowed opacity-50',
        )}
      >
        <input
          ref={inputRef}
          type="file"
          accept={acceptedTypes.join(',')}
          className="hidden"
          onChange={handleInputChange}
          disabled={disabled}
        />

        {hasImage && (
          <>
            <img
              src={value}
              alt={alt ?? t('catalog.imageUpload.previewAlt')}
              className="h-full w-full object-cover"
            />
            {isDragOver && !dragRejected && (
              <div className="text-white-0 absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-green-900/60">
                <ImageIcon className="h-8 w-8" />
                <span className="text-sm font-medium">
                  {t('catalog.imageUpload.dropToReplace')}
                </span>
              </div>
            )}
            {isDragOver && dragRejected && (
              <div className="text-white-0 absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-red-900/60">
                <span className="text-sm font-medium">
                  {t('catalog.imageUpload.dragRejected')}
                </span>
              </div>
            )}
            {!isUploading && onRemove && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
                className="bg-grey-800/70 text-white-0 hover:bg-grey-800/90 absolute top-1 right-1 rounded-full p-1"
                aria-label={t('catalog.imageUpload.cancelLabel')}
              >
                <Cross2Icon className="h-3 w-3" />
              </button>
            )}
            {isUploading && (
              <div className="bg-grey-900/50 absolute inset-0 flex flex-col items-center justify-center gap-2 p-4">
                <ProgressBar value={progress} showLabel className="w-full" />
                {onRemove && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove();
                    }}
                    className="bg-grey-800/70 text-white-0 hover:bg-grey-800/90 mt-1 rounded-full p-1"
                    aria-label={t('catalog.imageUpload.cancelLabel')}
                  >
                    <Cross2Icon className="h-3 w-3" />
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {!hasImage && isUploading && (
          <div className="flex w-full flex-col items-center gap-2 p-4">
            <ProgressBar value={progress} showLabel className="w-full" />
            {onRemove && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
                className="bg-grey-800/70 text-white-0 hover:bg-grey-800/90 mt-1 rounded-full p-1"
                aria-label={t('catalog.imageUpload.cancelLabel')}
              >
                <Cross2Icon className="h-3 w-3" />
              </button>
            )}
          </div>
        )}

        {!hasImage && !isUploading && (
          <div className="flex flex-col items-center gap-2 p-4 text-center">
            {dragRejected ? (
              <span className="text-sm text-red-500 dark:text-red-400">
                {t('catalog.imageUpload.dragRejected')}
              </span>
            ) : (
              <>
                <div className="text-text-tertiary flex flex-col items-center gap-1">
                  {isDragOver ? (
                    <ImageIcon className="h-8 w-8" />
                  ) : (
                    <UploadIcon className="h-8 w-8" />
                  )}
                  <span className="text-text-secondary text-sm font-medium">
                    {t('catalog.imageUpload.placeholder')}
                  </span>
                  <span className="text-text-tertiary text-xs">
                    {t('catalog.imageUpload.hint')}
                  </span>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {displayError && (
        <div className="mt-1 flex items-center gap-1 text-xs text-red-500 dark:text-red-400">
          <span className="flex items-center gap-1">
            <Cross2Icon className="h-3 w-3" />
            <small>{displayError}</small>
          </span>
        </div>
      )}
    </div>
  );
}
